import {Card, Col, Row, Select, Table} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import moment from "moment"
import {Loading} from "../../components/Loading/Loading"
import {limitOptionsSelect} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {useHistory, useLocation} from "react-router-dom"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {getPSLeakDeviceIndications, setPSLeakDeviceIndications, setPSLeakDeviceIndicationsFilter, setWSPSLeakDeviceIndication} from "../../store/psLeakDeviceIndicationReducer"
import {useGetCompanies, useGetDevices, useGetObjectGroups, useGetObjects} from "../../hooks/useFetchHooks"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"
import {FormInput} from "../../components/Form/FormInput"

export const PSLeakDeviceIndications = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const psLeakDeviceIndications = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.psLeakDeviceIndications)
    const filter = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.filter)
    const ws = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const objectGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const objectGroups = useGetObjectGroups(objectGroupsQuery)

    const objectsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
    }), [filter.idCompany, filter.idObjectGroup])
    const objects = useGetObjects(objectsQuery)

    const devicesQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
        idObject: filter.idObject,
    }), [filter.idCompany, filter.idObjectGroup, filter.idObject])
    const devices = useGetDevices(devicesQuery)

    const columns = useMemo(() => {
        const cols = []

        cols.push(
            {
                title: 'Дата',
                dataIndex: 'date',
                key: 'date',
                align: center,
            })

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: 'Компания',
            dataIndex: 'nameCompany',
            key: 'nameCompany',
            align: center,
        })

        cols.push(
            {
                title: 'Группа',
                dataIndex: 'nameObjectGroup',
                key: 'nameObjectGroup',
                align: center,
            },
            {
                title: 'Объект',
                dataIndex: 'nameObject',
                key: 'nameObject',
                align: center,
            },
            {
                title: 'Оборудование',
                dataIndex: 'nameDevice',
                key: 'nameDevice',
                align: center,
            },
            {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Значение',
                dataIndex: 'value',
                key: 'value',
            },
            {
                title: 'Ед. измерения',
                dataIndex: 'units',
                key: 'units',
            },
        )

        return cols
    }, [profile])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setPSLeakDeviceIndicationsFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getPSLeakDeviceIndicationsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getPSLeakDeviceIndications({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getPSLeakDeviceIndicationsFunc()

        return () => {
            dispatch(setPSLeakDeviceIndications(null))
        }
    }, [dispatch, getPSLeakDeviceIndicationsFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (profile?.idCompany !== ID_SYSTEM_COMPANY && filter?.idCompany !== profile?.idCompany) {
            history.replace({search: filterToString({...filter, idCompany: profile?.idCompany})})
        }
    }, [profile])

    useEffect(() => {
        if (ws) {
            getPSLeakDeviceIndicationsFunc(true)
        }
        dispatch(setWSPSLeakDeviceIndication(null))
    }, [dispatch, ws, getPSLeakDeviceIndicationsFunc])

    const data = useMemo(() => psLeakDeviceIndications?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            nameDevice: i.nameDevice,
            nameObject: i.nameObject,
            nameObjectGroup: i.nameObjectGroup,
            nameCompany: i.nameCompany,
            value: i.value,
            units: i.units,
            name: i.name,
            date: `${moment(i.date).format('DD.MM.YYYY HH:mm')} ${getTimeZoneDiffString(i.timeZone)}`,
        }
    }), [psLeakDeviceIndications])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const objectGroupsOptions = useMemo(() => objectGroups?.data?.map(i => ({label: i.name, value: i.id})), [objectGroups])
    const objectOptions = useMemo(() => objects?.data?.map(i => ({label: i.name, value: i.id})), [objects])
    const deviceOptions = useMemo(() => devices?.data?.map(i => ({label: i.name, value: i.id})), [devices])

    return (
        <div className="tabled">
            <Helmet>
                <title>Показания устройств</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title="Показания устройств"
                          extra={
                              <div className='table-title-extra'>
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ?
                                          <FormSelect value={filter?.idCompany}
                                                      containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                      dropdownStyle={{minWidth: 'min-content'}}
                                                      onChange={(value) => history.replace({
                                                          search: filterToString({
                                                              ...filter,
                                                              idCompany: value,
                                                              offset: 0,
                                                              idObjectGroup: '',
                                                              idObject: '',
                                                              idDevice: '',
                                                          }),
                                                      })}
                                                      options={companyOptions}
                                                      placeholder="Компания"
                                                      allowClear
                                                      placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idObjectGroup}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObjectGroup: value,
                                                      offset: 0,
                                                      idObject: '',
                                                      idDevice: '',
                                                  }),
                                              })}
                                              options={objectGroupsOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idObject}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObject: value,
                                                      offset: 0,
                                                      idDevice: '',
                                                  }),
                                              })}
                                              options={objectOptions}
                                              placeholder="Объект"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idDevice}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idDevice: value,
                                                      offset: 0,
                                                  }),
                                              })}
                                              options={deviceOptions}
                                              placeholder="Оборудование"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                psLeakDeviceIndications
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter?.limit ? {
                                                 position: psLeakDeviceIndications?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: psLeakDeviceIndications.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) => {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/psLeakDeviceIndication/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDeviceIndication/${id}`, '_blank', 'noopener,noreferrer')
                                             })}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
